import Vue from 'vue';

import i18n from '@/i18n';
import { call, getTwilioToken } from '@/services/voip';

import { disconnectAll, twilioDevice } from '@/services/twilio';

// Backend can't tell apart "this call is routed back to the operator who placed it" from
// "this call comes from an operator-type account calling the operator queue" - both are
// reported as From: 'client:central' (see apps/dialer/call.py::make_voip_call). So instead of
// inferring self-initiation from the caller identity, the frontend tracks its own intent.
export const SELF_CONNECT_TIMEOUT_MS = 15000;

export const state = {
  token: '',
  status: 'unregistered',
  isCalling: false,
  conference: null,
  callType: null,
  missedCalls: 0,
  deviation: null,
  microphoneMuted: false,
  awaitingSelfConnect: false,
};

export const mutations = {
  setToken(state, token) {
    state.token = token;
  },

  setStatus(state, status) {
    state.status = status;
  },

  setIsCalling(state, isCalling) {
    state.isCalling = isCalling;
  },

  setConference(state, conference) {
    state.conference = conference;
  },

  setCallType(state, type) {
    state.callType = type;
  },

  setMissedCalls(state, missedCalls) {
    state.missedCalls = missedCalls;
  },

  setDeviation(state, deviation) {
    state.deviation = deviation;
  },

  setMicrophoneMuted(state, microphoneMuted) {
    state.microphoneMuted = microphoneMuted;
  },

  setAwaitingSelfConnect(state, awaitingSelfConnect) {
    state.awaitingSelfConnect = awaitingSelfConnect;
  },
};

export const actions = {
  updateStatus({ commit, state }) {
    let status;

    if (state.deviation) {
      status = 'deviated';
    } else if (twilioDevice) {
      if (twilioDevice.isBusy) {
        status = 'busy';
      } else {
        status = twilioDevice.state;
      }
    } else {
      status = 'unregistered';
    }

    if (state.status !== status) {
      commit('setStatus', status);
    }
  },

  setDeviation({ commit, dispatch }, deviation) {
    commit('setDeviation', deviation);
    dispatch('updateStatus');
  },

  setMicrophoneMuted({ commit }, microphoneMuted) {
    commit('setMicrophoneMuted', microphoneMuted);
  },

  disconnectDevice() {
    disconnectAll();
  },

  getTwilioToken({ commit }) {
    return getTwilioToken().then(
      response => {
        commit('setToken', response.data);
      },
      error => {
        Vue.notify({
          type: 'error',
          title: i18n.t('voip.label'),
          text: error,
          duration: -1,
        });
      }
    );
  },

  async call({ commit }, { type, id }) {
    commit('setAwaitingSelfConnect', true);
    // Safety net: if Twilio never routes us back to our own conference (the call failed to
    // reach us, a network hiccup, ...), don't leave the flag stuck - otherwise the next
    // genuine incoming call would be mistaken for our own and auto-accepted.
    setTimeout(
      () => commit('setAwaitingSelfConnect', false),
      SELF_CONNECT_TIMEOUT_MS
    );

    const callback = response => {
      commit('setCallType', type);

      commit(
        'setMicrophoneMuted',
        response.data.conference_type === 'emergency'
      );
      commit('setConference', response.data);
    };

    return call(id, type)
      .catch(error => {
        commit('setAwaitingSelfConnect', false);
        throw error;
      })
      .then(callback);
  },

  increaseMissedCalls({ state, commit }) {
    commit('setMissedCalls', state.missedCalls + 1);
  },
};

export const getters = {
  participateInCurrentCall: state => username => {
    return (
      state.conference &&
      state.conference.invitees.filter(
        invitee => invitee.user.username === username
      ).length > 0
    );
  },
};

export default {
  namespaced: true,
  state,
  mutations,
  actions,
  getters,
};
